import React from 'react';
import { Button, Switch, CircularProgress } from '@material-ui/core';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import PaymentMethodsSection from '../stripe/PaymentMethodsSection';
import SavedCardPaymentDialog from '../stripe/SavedCardPaymentDialog';
import AuthUtil from '../../redux/AuthUtil';
import {
  STRIPE as STRIPE_ENDPOINTS,
  COMPANY as COMPANY_ENDPOINTS,
} from '../global/Server/endpoints';

const C = {
  navyA: '#0a0e2b',
  navyB: '#111b5e',
  navy: '#0f1847',
  navyCard: '#1a2666',
  blueDeep: '#111b5e',
  royalBlue: '#1a3a8f',
  gold: '#D4AF37',
  goldDark: '#B8960C',
  blueGray: '#8a9cc5',
  lightBlue: '#c5cfe8',
  blueTint: '#b0b8cc',
  empGray: '#5a6a8a',
  purple: '#8B6FC0',
  teal: '#22a06b',
  greenActive: '#16a34a',
  greenBg: '#f0fdf4',
  redAlert: '#dc2626',
  redBg: '#fef2f2',
  pageBg: '#f4f5f8',
  card: '#ffffff',
  border: '#edf0f5',
  rowDiv: '#f0f1f4',
};

const ROLE_COLORS = {
  OWNER: C.gold,
  MGR: C.royalBlue,
  SUPER: C.purple,
  READING: C.teal,
  EMP: C.empGray,
};

const ROLE_LABELS = {
  OWNER: 'OWNER',
  MGR: 'MGR',
  SUPER: 'SUPER',
  READING: 'READING',
  EMP: 'EMP',
};

function getInitials(name) {
  return (name || '')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function inferRole(user) {
  if (user.investorPercent) return 'OWNER';
  if (user.basePay || user.bonusPercent || user.managerThreshold) return 'MGR';
  return 'EMP';
}

function fmtDate(d) {
  if (!d) return '—';
  // Backend ships "yyyy-MM-dd" strings (Chicago calendar dates). Using
  // `new Date(d)` here would parse as UTC midnight and shift back a
  // day in the browser's local TZ — drift the same bug the cycle
  // formatters used to hit. Parse via the shared Chicago-aware helper.
  const dt = parseChicagoDate(d);
  if (!dt) return d;
  return dt.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

// Parse "2026-06-15" as a calendar date in America/Chicago and return
// a Date object whose local-TZ components match. Avoids the standard
// JS pitfall where new Date("2026-06-15") is interpreted as UTC
// midnight, then shown in the browser's TZ as the previous day.
function parseChicagoDate(s) {
  if (!s) return null;
  // Strip any time component the backend may include.
  const datePart = String(s).slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function parseDuration(duration) {
  if (typeof duration !== 'string') return 1;
  if (duration === '1Y' || duration === '12' || duration === '12M') return 12;
  const digits = duration.replace(/[^0-9]/g, '');
  return digits ? parseInt(digits, 10) || 1 : 1;
}

// "Jun 2026" — the billing-cycle month, derived from coversCycleEndDate.
// Cycles run 15th-to-15th. A row with end-date Jul 15 covers Jun 15 →
// Jul 15 — labeled by the cycle's START month, which is what the
// customer thinks of as "the month I'm paying for".
function fmtCycleMonth(d, duration) {
  const dt = parseChicagoDate(d);
  if (!dt) return '—';
  dt.setMonth(dt.getMonth() - parseDuration(duration));
  return dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// "Jun 15 – Jul 15" — the date range the cycle covers. Cycle start =
// cycleEnd - duration months. Renders without the year since the year
// is already shown in the main cycle label above it.
function fmtCycleRange(d, duration) {
  const end = parseChicagoDate(d);
  if (!end) return null;
  const start = new Date(end);
  start.setMonth(start.getMonth() - parseDuration(duration));
  const startStr = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const endStr = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return `${startStr} – ${endStr}`;
}

// "Apr 30" / "May 15" — short, no year. Used as the muted second line under
// the cycle month to show when paid (or when due for unpaid rows).
function fmtShortDate(d) {
  const dt = parseChicagoDate(d);
  if (!dt) return null;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Display label for a saved Stripe PaymentMethod row — used by the
// auto-renewal banner. Wallets and Link expose different fields than a
// raw card, so handle those before falling back to brand + last4.
function cardLabel(m) {
  if (!m) return null;
  if (m.type === 'link' || m.walletType === 'link') {
    return m.linkEmail ? `Link · ${m.linkEmail}` : 'Link';
  }
  if (m.walletType === 'apple_pay') return 'Apple Pay';
  if (m.walletType === 'google_pay') return 'Google Pay';
  const brand = (m.brand || 'card').toString();
  const brandLabel = brand.charAt(0).toUpperCase() + brand.slice(1);
  return m.last4 ? `${brandLabel} •••• ${m.last4}` : brandLabel;
}

// Icon for the auto-renewal banner. Matches the picker's styling so the
// admin recognises the same card across views.
function cardIcon(m) {
  if (!m) return <CreditCardIcon style={{ fontSize: 16, color: '#94a3b8' }} />;
  if (m.type === 'link' || m.walletType === 'link')
    return <CreditCardIcon style={{ fontSize: 16, color: '#00d66f' }} />;
  if (m.walletType === 'apple_pay')
    return <AppleIcon style={{ fontSize: 16, color: '#0f172a' }} />;
  if (m.walletType === 'google_pay')
    return <AndroidIcon style={{ fontSize: 16, color: '#4285F4' }} />;
  return <CreditCardIcon style={{ fontSize: 16, color: '#4338ca' }} />;
}

class LocationDetailView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: 'subscription',
      // Pay-from-history dialog state. payRow carries the LicensePayment row
      // the user clicked Pay on, so the dialog charges the right amount and
      // we can refresh after success.
      payDialogOpen: false,
      payRow: null,
      // Bumped after every successful Pay. PaymentMethodsSection
      // watches this and refetches its saved-cards list — without it,
      // a "Save card for future payments" pay flow attaches a new
      // PaymentMethod server-side but the UI section keeps showing the
      // pre-pay list until the page is hard-refreshed.
      paymentMethodsRefreshSignal: 0,
      // Default card shown in the auto-renewal banner above Payment History.
      // Fetched once per Location open; refreshed when the dialog closes
      // (in case the admin just added or changed cards).
      defaultCard: null,
      // Optimistic state for the Billing Details auto-renewal toggle.
      // null = show locationDetail.autoRenewal as the source of truth.
      // boolean = override during in-flight server write.
      autoRenewalOverride: null,
      autoRenewalSaving: false,
      // Optimistic state for the Billing Details duration selector.
      // null = show locationDetail.billingDuration as the source of truth.
      billingDurationOverride: null,
      billingDurationSaving: false,
      // Fresh next-cycle preview returned by setBillingDuration, shown
      // until the locationDetail refetch catches up. Cleared whenever
      // locationDetail refreshes (see componentDidUpdate).
      billingPreviewOverride: null,
    };
  }

  componentDidMount() {
    this.fetchDefaultCard();
  }

  componentDidUpdate(prev) {
    const prevId = prev.location?.companyId;
    const curId = this.props.location?.companyId;
    if (prevId !== curId) {
      // Clear the previous location's card AND any optimistic toggle
      // override so we don't render KINGDOM's Visa or auto-renewal state
      // while the admin is staring at GREEN HOUSE's payment history.
      this.setState({
        defaultCard: null,
        autoRenewalOverride: null,
        autoRenewalSaving: false,
        billingDurationOverride: null,
        billingDurationSaving: false,
        billingPreviewOverride: null,
      });
      this.fetchDefaultCard();
    } else if (
      prev.locationDetail !== this.props.locationDetail &&
      this.state.billingPreviewOverride
    ) {
      // Fresh locationDetail carries its own nextBillingPreview — drop the
      // response-time override so later events (e.g. a Pay moving the
      // anchor) can't be masked by a stale snapshot.
      this.setState({ billingPreviewOverride: null });
    }
  }

  fetchDefaultCard = async () => {
    const companyId =
      this.props.location?.companyId ||
      this.props.locationDetail?.company?.companyId;
    if (!companyId) return;
    // Stale-response guard — drop the result if the admin switched to a
    // different location before this fetch landed.
    this._defaultCardReqId = (this._defaultCardReqId || 0) + 1;
    const reqId = this._defaultCardReqId;
    try {
      const res = await AuthUtil.postRequest(
        STRIPE_ENDPOINTS.LIST_SAVED_CARDS,
        { companyId }
      );
      if (this._defaultCardReqId !== reqId) return;
      const methods = res?.data?.methods || [];
      const defaultId = res?.data?.defaultPaymentMethodId;
      // Prefer the dashboard-marked default; fall back to the first saved
      // card so the banner still has something to show in the typical
      // "one card on file, admin never set default" state.
      const def = methods.find((m) => m.id === defaultId) || methods[0] || null;
      this.setState({ defaultCard: def });
    } catch (e) {
      if (this._defaultCardReqId !== reqId) return;
      this.setState({ defaultCard: null });
    }
  };

  handlePayUnpaid = (row) => {
    this.setState({ payDialogOpen: true, payRow: row });
  };

  handlePaySuccess = () => {
    // Re-fetch silently so when the user clicks Done the UI is fresh —
    // but DON'T close the dialog here. The success state ("Payment
    // successful" + Done button) needs to linger so the admin actually
    // sees the confirmation. Dismissal goes through handlePayDialogClose
    // when they click Done (or the X).
    const companyId =
      this.props.location?.companyId ||
      this.props.locationDetail?.company?.companyId;
    if (companyId && this.props.fetchLocationDetail) {
      this.props.fetchLocationDetail({ locationId: companyId });
    }
    // Recompute trial-grace on the auth user — paying the post-trial
    // row clears the banner / lockout on the next render.
    if (this.props.refreshUserInfo) this.props.refreshUserInfo();
    // Bump the PaymentMethodsSection refresh signal. If the user ticked
    // "Save card for future payments", the Pay flow attached a new PM
    // server-side — refetching here surfaces it immediately. The signal
    // is bumped unconditionally because Pay can save a card even when
    // the user used a saved one (e.g. Apple Pay tokenisation creates a
    // fresh PM on file).
    this.setState((s) => ({
      paymentMethodsRefreshSignal: (s.paymentMethodsRefreshSignal || 0) + 1,
    }));
  };

  handleCardAdded = () => {
    // Adding a card no longer implicitly enables autoRenewal — that's a
    // separate, explicit toggle the admin owns. Refresh locationDetail
    // (autoRenewal toggle source), userInfo (trial-grace banner source),
    // AND the default-card banner so the new card shows up immediately,
    // but DO NOT optimistically flip autoRenewalOverride: the server
    // hasn't enabled autoRenewal, so faking it on the client would lie
    // to the toggle until the next fetchLocationDetail snaps it back to
    // off and confuses the user.
    const companyId =
      this.props.location?.companyId ||
      this.props.locationDetail?.company?.companyId;
    if (companyId && this.props.fetchLocationDetail) {
      this.props.fetchLocationDetail({ locationId: companyId });
    }
    if (this.props.refreshUserInfo) this.props.refreshUserInfo();
    this.fetchDefaultCard();
  };

  handlePayDialogClose = () => {
    this.setState((s) => ({
      payDialogOpen: false,
      payRow: null,
      // Second bump on close — catches any tail between confirmPayment
      // and Stripe's PaymentMethod list reflecting the just-attached
      // card. handlePaySuccess already bumped once when the poll
      // returned activated:true, but if that fetch raced ahead of
      // Stripe's propagation a second refetch a few seconds later
      // (user reading "Payment successful" + clicking Done) safely
      // surfaces the new card.
      paymentMethodsRefreshSignal: (s.paymentMethodsRefreshSignal || 0) + 1,
    }));
    // The admin may have added a card inside the pay picker — refresh
    // the banner so it reflects the latest default.
    this.fetchDefaultCard();
    // Adding a card may also satisfy the trial-grace payment-method
    // requirement; re-fetch userInfo so the banner reacts.
    if (this.props.refreshUserInfo) this.props.refreshUserInfo();
  };

  handleAutoRenewalToggle = async (event) => {
    const enabled = !!event?.target?.checked;
    const companyId =
      this.props.location?.companyId ||
      this.props.locationDetail?.company?.companyId;
    if (!companyId) return;
    // Optimistic flip so the switch responds instantly even on a slow
    // network. Revert on failure.
    this.setState({ autoRenewalOverride: enabled, autoRenewalSaving: true });
    try {
      const res = await AuthUtil.postRequest(
        COMPANY_ENDPOINTS.SET_AUTO_RENEWAL,
        {
          companyId,
          autoRenewal: enabled,
        }
      );
      const newValue = res?.data?.autoRenewal === true;
      this.setState({
        autoRenewalOverride: newValue,
        autoRenewalSaving: false,
      });
      // Refresh locationDetail so the banner above Payment History and any
      // other consumer of locationDetail.autoRenewal stay consistent.
      if (this.props.fetchLocationDetail) {
        this.props.fetchLocationDetail({ locationId: companyId });
      }
      // Trial-grace banner depends on autoRenewal state; re-fetch
      // userInfo so the global banner hides as soon as the toggle
      // (combined with a saved card) satisfies the requirement.
      if (this.props.refreshUserInfo) this.props.refreshUserInfo();
    } catch (e) {
      // Revert optimistic flip.
      this.setState({
        autoRenewalOverride: !enabled,
        autoRenewalSaving: false,
      });
    }
  };

  handleBillingDurationChange = async (duration) => {
    const companyId =
      this.props.location?.companyId ||
      this.props.locationDetail?.company?.companyId;
    if (!duration || !companyId) return;
    if (this.state.billingDurationSaving) return;
    const previous =
      this.state.billingDurationOverride ||
      this.props.locationDetail?.billingDuration ||
      null;
    if (duration === previous) return;
    // Optimistic flip so the control responds instantly; reverts on failure.
    this.setState({
      billingDurationOverride: duration,
      billingDurationSaving: true,
    });
    try {
      const res = await AuthUtil.postRequest(
        COMPANY_ENDPOINTS.SET_BILLING_DURATION,
        { companyId, duration }
      );
      if (res?.data?.success === false) throw new Error('rejected');
      this.setState({
        billingDurationOverride: res?.data?.billingDuration || duration,
        // The response carries the recomputed next-cycle preview — show
        // the new amount immediately instead of waiting on the refetch.
        billingPreviewOverride: res?.data?.nextBillingPreview || null,
        billingDurationSaving: false,
      });
      // The server re-aligned the upcoming unpaid row (new duration +
      // amount) — refetch so the payment history's next Pay action and
      // the synthetic next-cycle row reflect it immediately.
      if (this.props.fetchLocationDetail) {
        this.props.fetchLocationDetail({ locationId: companyId });
      }
    } catch (e) {
      this.setState({
        billingDurationOverride: previous,
        billingDurationSaving: false,
      });
    }
  };

  // Freshest next-cycle preview: the setBillingDuration response override
  // (instant feedback) until the locationDetail refetch replaces it.
  getBillingPreview() {
    return (
      this.state.billingPreviewOverride ||
      this.props.locationDetail?.nextBillingPreview ||
      null
    );
  }

  render() {
    const { locationDetail, location } = this.props;
    const { tab } = this.state;

    // Fallback when locationDetail couldn't load (no users on the location,
    // a backend error, etc.) but the user did pick a company from the
    // previous screen — we still know its companyId from `location`. Render
    // a minimal version with the Payment Methods card so the admin can at
    // least save a card for the upcoming subscription. The full Subscription
    // / Team tabs depend on backend-shaped data and are hidden in this mode.
    if (!locationDetail) {
      const fallbackId = location?.companyId;
      const roles = this.props.user?.authority || [];
      const canBill =
        roles.includes('ROLE_OWNER') ||
        roles.includes('ROLE_TECH_ADMIN') ||
        roles.includes('ROLE_MASTER_ADMIN') ||
        roles.includes('ROLE_MANAGER');
      if (!fallbackId) {
        return (
          <div
            style={{
              minHeight: '100vh',
              background: C.pageBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{ color: C.blueGray }}>Loading...</div>
          </div>
        );
      }
      const cardStyle = {
        background: '#fff',
        borderRadius: 12,
        padding: 14,
        marginBottom: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        border: '1px solid #edf0f5',
      };
      return (
        <div
          style={{
            minHeight: '100vh',
            background: C.pageBg,
            fontFamily: "'SF Pro Display','Segoe UI',-apple-system,sans-serif",
          }}
        >
          <div
            style={{
              background: `linear-gradient(135deg, ${C.navy}, ${C.navyCard})`,
              padding: '16px 14px',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.5,
                color: C.gold,
                textTransform: 'uppercase',
              }}
            >
              My Account
            </div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: '#fff',
                marginTop: 2,
              }}
            >
              {location?.name || 'Location'}
            </div>
            <div style={{ fontSize: 12, color: C.lightBlue, marginTop: 4 }}>
              Subscription details are unavailable right now. You can still
              manage saved payment methods below.
            </div>
          </div>
          <div style={{ padding: 12 }}>
            {canBill && (
              <PaymentMethodsSection
                companyId={fallbackId}
                colors={C}
                cardStyle={cardStyle}
                onCardAdded={this.handleCardAdded}
                refreshSignal={this.state.paymentMethodsRefreshSignal}
              />
            )}
            {!canBill && (
              <div
                style={{
                  ...cardStyle,
                  color: C.blueGray,
                  fontSize: 13,
                  textAlign: 'center',
                }}
              >
                Payment management is limited to owners and admins.
              </div>
            )}
          </div>
        </div>
      );
    }

    const { company, currentSubscription, paymentHistory, profile } =
      locationDetail;
    const roles = this.props.user?.authority || [];
    const isActive = currentSubscription?.isActive;
    const planName = company?.planName || location?.planName || 'Not set';
    const price = currentSubscription?.amount || location?.amount || 0;

    // Synthesize an UNPAID row for the very next cycle the customer
    // owes. Anchored on the latest paid expiryDate (= the cycle they're
    // paid through), so the synthetic is always exactly one cycle past
    // that — never skips a month and never duplicates when they've
    // pre-paid ahead. Once a real payment row exists for the target
    // cycle, the synthetic vanishes.
    //
    // Pre-pay window: the synthetic only renders when its dueDate falls
    // within the current or next calendar month. In June you can pre-pay
    // July's bill (due Jul 15); once that's paid, August's bill (due
    // Aug 15) stays hidden until July arrives — pre-paying never marches
    // the row endlessly into the future. Rows for cycles already due or
    // overdue are never gated (dueDate in the past always shows).
    const canBillForLocation =
      roles.includes('ROLE_OWNER') ||
      roles.includes('ROLE_TECH_ADMIN') ||
      roles.includes('ROLE_MASTER_ADMIN') ||
      roles.includes('ROLE_MANAGER');
    let syntheticRow = null;
    if (canBillForLocation) {
      const latest = (paymentHistory || []).reduce((max, p) => {
        if (!p?.expiryDate) return max;
        if (!max || new Date(p.expiryDate) > new Date(max.expiryDate)) return p;
        return max;
      }, null);

      // Server-computed preview of the next cycle under the company's
      // billing-duration preference (cycle end, due date, duration,
      // amount). Preferred over the local +1-month math below so a
      // duration change (3M/6M/1Y) is reflected in the synthetic row —
      // and in the amount the Pay dialog charges — without the client
      // re-implementing the backend's cycle bucketing or discounts.
      // getBillingPreview() also covers the moment right after a duration
      // change, before the locationDetail refetch lands.
      const preview = this.getBillingPreview();

      let targetEnd = null;
      let dueDate = null;
      if (preview?.cycleEndDate) {
        targetEnd = String(preview.cycleEndDate).slice(0, 10);
        dueDate = preview.dueDate
          ? String(preview.dueDate).slice(0, 10)
          : latest?.expiryDate
          ? latest.expiryDate.slice(0, 10)
          : null;
      } else if (latest?.expiryDate) {
        // Next cycle's dueDate = latest paid expiry (= cycle start),
        // targetEnd = one month after that. Anchoring on latest paid
        // means a customer who already pre-paid June surfaces July
        // here, while one who only paid May surfaces June — without
        // either case skipping or doubling a month.
        const latestStr = latest.expiryDate.slice(0, 10);
        const [yStr, mStr] = latestStr.split('-');
        const latestYear = parseInt(yStr, 10);
        const latestMonth = parseInt(mStr, 10); // 1-indexed
        let nextMonth = latestMonth + 1;
        let nextYear = latestYear;
        if (nextMonth > 12) {
          nextMonth -= 12;
          nextYear += 1;
        }
        targetEnd = `${nextYear}-${String(nextMonth).padStart(2, '0')}-15`;
        dueDate = latestStr;
      } else {
        // No history — fall back to "next 15th from now" so brand-new
        // companies still see a pre-pay row.
        const now = new Date();
        const targetMonthIdx = now.getMonth() + 1;
        const targetYear = now.getFullYear() + Math.floor(targetMonthIdx / 12);
        const monthMod = ((targetMonthIdx % 12) + 12) % 12;
        targetEnd = `${targetYear}-${String(monthMod + 1).padStart(2, '0')}-15`;
        const dueMonthIdx = now.getMonth();
        const dueYear = now.getFullYear() + Math.floor(dueMonthIdx / 12);
        const dueMonthMod = ((dueMonthIdx % 12) + 12) % 12;
        dueDate = `${dueYear}-${String(dueMonthMod + 1).padStart(2, '0')}-15`;
      }

      const alreadyPaid = (paymentHistory || []).some(
        (p) => (p.expiryDate || '').slice(0, 10) === targetEnd
      );

      // A real future-dated UNPAID row already gives the admin a Pay
      // action for the upcoming cycle — the synthetic exists only to fill
      // the gap when no such row exists. Without this guard a pending row
      // whose cycle end differs slightly from the recomputed preview
      // (e.g. a multi-month trial row) would show up TWICE. Past-due
      // pending rows don't suppress — they're debt for consumed cycles,
      // not the next one.
      const now2 = new Date();
      const todayStr = `${now2.getFullYear()}-${String(
        now2.getMonth() + 1
      ).padStart(2, '0')}-${String(now2.getDate()).padStart(2, '0')}`;
      const hasFuturePendingRow = (paymentHistory || []).some(
        (p) =>
          p.collectionStatus === 'pending' &&
          (p.expiryDate || '').slice(0, 10) > todayStr
      );

      // Latest dueDate allowed to surface = the 15th of NEXT month.
      // yyyy-mm-dd strings compare lexically the same as chronologically.
      const now = new Date();
      const maxDueMonthIdx = now.getMonth() + 1; // 0-indexed next month
      const maxDueYear = now.getFullYear() + Math.floor(maxDueMonthIdx / 12);
      const maxDueMonth = (maxDueMonthIdx % 12) + 1; // back to 1-indexed
      const maxDue = `${maxDueYear}-${String(maxDueMonth).padStart(2, '0')}-15`;
      const withinPrepayWindow = !dueDate || dueDate <= maxDue;

      if (!alreadyPaid && !hasFuturePendingRow && withinPrepayWindow) {
        const duration =
          preview?.duration ||
          latest?.duration ||
          currentSubscription?.duration ||
          '1M';
        const amount =
          preview?.amount ||
          (latest && latest.amount) ||
          currentSubscription?.amount ||
          price ||
          '';
        syntheticRow = {
          paymentId: '__synthetic_next__',
          expiryDate: targetEnd,
          coversCycleEndDate: targetEnd,
          dueDate,
          duration,
          amount,
          collectionStatus: 'pending',
          __synthetic: true,
        };
      }
    }
    const displayedHistory = syntheticRow
      ? [syntheticRow, ...(paymentHistory || [])]
      : paymentHistory || [];

    // Merge people data
    const owners = profile?.localAdmins || [];
    const managers = profile?.managers || [];
    const employees = profile?.employees || [];
    const emergency = profile?.emergencyContacts?.filter((e) => e.name) || [];

    const ownerCount = owners.length;
    const mgrCount = managers.length;
    const empCount = employees.length;

    const font = "'SF Pro Display','Segoe UI',-apple-system,sans-serif";

    return (
      <div
        style={{ minHeight: '100vh', background: C.pageBg, fontFamily: font }}
      >
        {/* Company Hero */}
        <div
          style={{
            background: `linear-gradient(135deg, ${C.navy}, ${C.navyCard})`,
            padding: '16px 14px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                flexShrink: 0,
                background: 'rgba(212,175,55,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: C.gold,
                fontSize: 18,
                fontWeight: 800,
              }}
            >
              {getInitials(company?.name || location?.name)}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>
                {company?.name || location?.name}
              </div>
              <div style={{ fontSize: 13, color: C.blueGray }}>
                {planName}{' '}
                {location?.companyCode ? `· #${location.companyCode}` : ''}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 10,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '3px 10px',
                borderRadius: 4,
                color: '#fff',
                background: isActive ? C.greenActive : C.redAlert,
              }}
            >
              {isActive ? 'ACTIVE' : 'UNPAID'}
            </span>
            <span style={{ fontSize: 12, color: C.blueGray }}>
              Next billing: {fmtDate(currentSubscription?.expiryDate)}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            background: C.card,
            borderBottom: `2px solid ${C.border}`,
          }}
        >
          {['subscription', 'team'].map((t) => (
            <div
              key={t}
              onClick={() => this.setState({ tab: t })}
              style={{
                flex: 1,
                padding: '10px 0',
                textAlign: 'center',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 700,
                color: tab === t ? C.gold : C.blueGray,
                borderBottom: `3px solid ${tab === t ? C.gold : 'transparent'}`,
                transition: 'all .15s',
              }}
            >
              {t === 'subscription' ? 'Subscription' : 'Team'}
            </div>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ padding: '12px 14px' }}>
          {/* ── SUBSCRIPTION TAB ── */}
          {tab === 'subscription' && (
            <>
              {/* Current Plan */}
              <div
                style={{
                  ...cardStyle,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: C.navy }}>
                    {planName}
                  </div>
                  <div>
                    <span
                      style={{ fontSize: 22, fontWeight: 800, color: C.gold }}
                    >
                      ${price}
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: C.blueGray,
                      }}
                    >
                      /mo
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: C.blueGray,
                    textAlign: 'right',
                  }}
                >
                  Manage subscription
                  <br />
                  in Settings
                </div>
              </div>

              {/* Billing Grid */}
              <div style={{ ...cardStyle }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.blueGray,
                    marginBottom: 8,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Billing Details
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '8px 14px',
                  }}
                >
                  {[
                    {
                      label: 'Status',
                      value: isActive ? 'Active' : 'Unpaid',
                      color: isActive ? C.greenActive : C.redAlert,
                    },
                    {
                      label: 'Start Date',
                      value: fmtDate(currentSubscription?.startDate),
                    },
                    {
                      label: 'Expiry Date',
                      value: fmtDate(currentSubscription?.expiryDate),
                    },
                  ].map((item) => (
                    <div key={item.label}>
                      <div style={{ fontSize: 11, color: C.blueGray }}>
                        {item.label}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: item.color || C.navy,
                        }}
                      >
                        {item.value}
                      </div>
                    </div>
                  ))}

                  {/* Auto-renewal toggle — single source of truth shared
                      with the cron and the Payment History banner. Optimistic
                      update on click so the switch is snappy; reverts if
                      the server write fails. Placed before the full-width
                      duration row so it fills the grid slot next to
                      Expiry Date. */}
                  {(() => {
                    const serverFlag = locationDetail.autoRenewal === true;
                    const flag =
                      this.state.autoRenewalOverride != null
                        ? this.state.autoRenewalOverride
                        : serverFlag;
                    return (
                      <div>
                        <div style={{ fontSize: 11, color: C.blueGray }}>
                          Auto-Renewal
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            marginTop: -2,
                          }}
                        >
                          <Switch
                            size="small"
                            color="primary"
                            checked={flag}
                            disabled={this.state.autoRenewalSaving}
                            onChange={this.handleAutoRenewalToggle}
                          />
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: flag ? C.greenActive : C.blueGray,
                            }}
                          >
                            {flag ? 'On' : 'Off'}
                          </span>
                          {this.state.autoRenewalSaving && (
                            <CircularProgress
                              size={12}
                              style={{ marginLeft: 4 }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Duration selector — billing roles pick the length of
                      the NEXT cycle here (1/3/6/12 months). The server
                      persists it on the Company and re-aligns the upcoming
                      unpaid row, so the next Pay amount — and the 15th
                      auto-renewal charge — follow the new duration. The
                      active license is never touched. */}
                  {(() => {
                    const monthsToCode = {
                      1: '1M',
                      3: '3M',
                      6: '6M',
                      12: '1Y',
                    };
                    const serverDuration =
                      locationDetail.billingDuration ||
                      currentSubscription?.duration;
                    const value =
                      this.state.billingDurationOverride ||
                      monthsToCode[parseDuration(serverDuration)] ||
                      '1M';
                    const labels = {
                      '1M': '1 Month',
                      '3M': '3 Months',
                      '6M': '6 Months',
                      '1Y': '1 Year',
                    };
                    if (!canBillForLocation) {
                      return (
                        <div>
                          <div style={{ fontSize: 11, color: C.blueGray }}>
                            Duration
                          </div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: C.navy,
                            }}
                          >
                            {labels[value] || value}
                          </div>
                        </div>
                      );
                    }

                    const saving = this.state.billingDurationSaving;
                    const preview = this.getBillingPreview();
                    const previewDue = preview?.dueDate
                      ? fmtShortDate(preview.dueDate)
                      : null;
                    const options = [
                      { code: '1M', n: '1', unit: 'MONTH' },
                      { code: '3M', n: '3', unit: 'MONTHS' },
                      { code: '6M', n: '6', unit: 'MONTHS' },
                      { code: '1Y', n: '1', unit: 'YEAR' },
                    ];
                    return (
                      <div style={{ gridColumn: '1 / -1', marginTop: 2 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            marginBottom: 6,
                          }}
                        >
                          <div style={{ fontSize: 11, color: C.blueGray }}>
                            Billing Duration
                          </div>
                          {saving && <CircularProgress size={11} />}
                          <span style={{ flex: 1 }} />
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '2px 7px',
                              borderRadius: 10,
                              background: 'rgba(212,175,55,0.12)',
                              color: C.goldDark,
                              letterSpacing: 0.3,
                            }}
                          >
                            NEXT CYCLE
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: 6,
                            opacity: saving ? 0.55 : 1,
                            pointerEvents: saving ? 'none' : 'auto',
                            transition: 'opacity .15s',
                          }}
                        >
                          {options.map((opt) => {
                            const active = value === opt.code;
                            return (
                              <div
                                key={opt.code}
                                role="button"
                                tabIndex={0}
                                onClick={() =>
                                  this.handleBillingDurationChange(opt.code)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    this.handleBillingDurationChange(opt.code);
                                  }
                                }}
                                style={{
                                  flex: 1,
                                  padding: '8px 2px 7px',
                                  borderRadius: 10,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  outline: 'none',
                                  border: active
                                    ? `1.5px solid ${C.navy}`
                                    : '1.5px solid #e2e6f0',
                                  background: active
                                    ? `linear-gradient(135deg, ${C.navy}, ${C.navyCard})`
                                    : '#fff',
                                  boxShadow: active
                                    ? '0 4px 10px rgba(15,24,71,0.22)'
                                    : 'none',
                                  transition: 'all .15s',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 16,
                                    fontWeight: 800,
                                    lineHeight: 1.1,
                                    color: active ? C.gold : C.navy,
                                  }}
                                >
                                  {opt.n}
                                </div>
                                <div
                                  style={{
                                    fontSize: 8.5,
                                    fontWeight: 700,
                                    letterSpacing: 0.8,
                                    marginTop: 1,
                                    color: active
                                      ? 'rgba(255,255,255,0.85)'
                                      : C.blueGray,
                                  }}
                                >
                                  {opt.unit}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: C.blueGray,
                            marginTop: 12,
                          }}
                        >
                          {preview?.amount ? (
                            <>
                              Next payment{' '}
                              <span style={{ fontWeight: 800, color: C.navy }}>
                                ${preview.amount}
                              </span>
                              {previewDue ? ` · due ${previewDue}` : ''} — the
                              current license is unchanged.
                            </>
                          ) : (
                            'Applies to the next payment — the current license is unchanged.'
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Emergency Contacts */}
              {roles.includes('ROLE_OWNER') && emergency.length > 0 && (
                <div style={cardStyle}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: C.blueGray,
                      marginBottom: 8,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Emergency Contacts
                  </div>
                  {emergency.map((c, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom:
                          i < emergency.length - 1
                            ? `1px solid ${C.rowDiv}`
                            : 'none',
                      }}
                    >
                      <span
                        style={{ fontSize: 14, fontWeight: 600, color: C.navy }}
                      >
                        {c.name || '—'}
                      </span>
                      {c.phone && (
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: C.royalBlue,
                          }}
                        >
                          {c.phone}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Phase 2: Payment Methods — gated to billing roles.
                  Lets owners + NDS admins save a card for auto-renewal,
                  set a default, and trigger an on-session Pay Now for the
                  next cycle. */}
              {(roles.includes('ROLE_OWNER') ||
                roles.includes('ROLE_TECH_ADMIN') ||
                roles.includes('ROLE_MASTER_ADMIN')) && (
                <PaymentMethodsSection
                  companyId={company?.companyId || location?.companyId}
                  colors={C}
                  cardStyle={cardStyle}
                  onCardAdded={this.handleCardAdded}
                  refreshSignal={this.state.paymentMethodsRefreshSignal}
                />
              )}

              {/* Payment History */}
              <div style={cardStyle}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.blueGray,
                    marginBottom: 8,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  Payment History
                </div>

                {/* Auto-renewal banner. Surfaces the saved card the 15th-of-
                    month cron will charge. The "auto-renewal ON" signal
                    comes from locationDetail.autoRenewal (same flag the
                    cron reads). Reflects the optimistic toggle override
                    so flipping the switch in Billing Details updates this
                    banner instantly. */}
                {(() => {
                  const serverFlag = locationDetail.autoRenewal === true;
                  const isAutoOn =
                    this.state.autoRenewalOverride != null
                      ? this.state.autoRenewalOverride
                      : serverFlag;
                  const card = this.state.defaultCard;
                  if (!isAutoOn) {
                    return (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 10px',
                          marginBottom: 10,
                          background: '#fff7ed',
                          border: '1px solid #fed7aa',
                          borderRadius: 8,
                          fontSize: 12,
                          color: '#9a3412',
                        }}
                      >
                        <AutorenewIcon style={{ fontSize: 16 }} />
                        <span>
                          <span style={{ fontWeight: 700 }}>
                            Auto-renewal off.
                          </span>{' '}
                          Payment will need to be made manually each month.
                        </span>
                      </div>
                    );
                  }
                  if (!card) {
                    return (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 10px',
                          marginBottom: 10,
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: 8,
                          fontSize: 12,
                          color: '#b91c1c',
                        }}
                      >
                        <AutorenewIcon style={{ fontSize: 16 }} />
                        <span>
                          <span style={{ fontWeight: 700 }}>
                            Auto-renewal on, but no card on file.
                          </span>{' '}
                          Add a payment method to avoid losing access on the
                          renewal date.
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        marginBottom: 10,
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: 8,
                        fontSize: 12,
                        color: '#166534',
                      }}
                    >
                      <AutorenewIcon
                        style={{ fontSize: 16, color: '#16a34a' }}
                      />
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>
                          Auto-renewal on.
                        </span>
                        Will charge
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '1px 6px',
                            borderRadius: 4,
                            background: '#fff',
                            border: '1px solid #d1fae5',
                            color: '#0f172a',
                            fontWeight: 600,
                          }}
                        >
                          {cardIcon(card)}
                          {cardLabel(card)}
                        </span>
                        on the 15th.
                      </span>
                    </div>
                  );
                })()}

                {/* Header */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 64px 64px 70px 56px',
                    columnGap: 10,
                    padding: '6px 0',
                    borderBottom: `1.5px solid ${C.border}`,
                    fontSize: 11,
                    fontWeight: 800,
                    color: C.blueGray,
                    textTransform: 'uppercase',
                  }}
                >
                  <div>Period</div>
                  <div style={{ textAlign: 'right' }}>Amount</div>
                  <div style={{ textAlign: 'right' }}>Duration</div>
                  <div style={{ textAlign: 'right' }}>Status</div>
                  <div style={{ textAlign: 'right' }}>Action</div>
                </div>
                {/* Rows. collectionStatus drives the badge:
                    null|'collected' = settled; 'pending' = unpaid (admin can
                    charge it from here); 'uncollectible' = written off.
                    The first row may be a synthetic next-cycle UNPAID
                    entry — its Pay Now button opens the manual payment
                    form above instead of the saved-card dialog. */}
                {displayedHistory.length > 0 ? (
                  displayedHistory.map((p, i) => {
                    const isTrial = p.amount === '0' || p.amount === 0;
                    const isPending = p.collectionStatus === 'pending';
                    const isUncollectible =
                      p.collectionStatus === 'uncollectible';
                    const isPaid = !isTrial && !isPending && !isUncollectible;
                    let label, badgeBg, badgeColor, amtColor;
                    if (isTrial) {
                      label = 'TRIAL';
                      badgeBg = C.greenBg;
                      badgeColor = C.greenActive;
                      amtColor = C.greenActive;
                    } else if (isPaid) {
                      label = 'PAID';
                      badgeBg = C.greenBg;
                      badgeColor = C.greenActive;
                      amtColor = C.navy;
                    } else if (isUncollectible) {
                      label = 'WRITTEN OFF';
                      badgeBg = C.redBg;
                      badgeColor = C.redAlert;
                      amtColor = C.redAlert;
                    } else {
                      label = 'UNPAID';
                      badgeBg = C.redBg;
                      badgeColor = C.redAlert;
                      amtColor = C.redAlert;
                    }
                    return (
                      <div
                        key={p.paymentId || i}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 64px 64px 70px 56px',
                          columnGap: 10,
                          padding: '8px 0',
                          alignItems: 'center',
                          borderBottom:
                            i < displayedHistory.length - 1
                              ? `1px solid ${C.rowDiv}`
                              : 'none',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: C.navy,
                            }}
                          >
                            {fmtCycleMonth(
                              p.expiryDate || p.startDate,
                              p.duration
                            )}
                          </div>
                          {(() => {
                            const range = fmtCycleRange(
                              p.expiryDate,
                              p.duration
                            );
                            return range ? (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: C.blueGray,
                                  marginTop: 1,
                                }}
                              >
                                {range}
                              </div>
                            ) : null;
                          })()}
                          {(() => {
                            // Subtitle: when paid (recorded) for settled rows,
                            // when due (cycle end OR trial-grace 3-day
                            // deadline) for unpaid ones. Trial rows show the
                            // free-period label instead.
                            const paidShort = fmtShortDate(p.startDate);
                            const dueShort = fmtShortDate(
                              p.dueDate || p.expiryDate
                            );
                            if (p.collectionStatus === 'trial') {
                              return (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: C.blueGray,
                                    fontWeight: 600,
                                  }}
                                >
                                  Free trial
                                </div>
                              );
                            }
                            if (isPending) {
                              return dueShort ? (
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: C.redAlert,
                                    fontWeight: 600,
                                  }}
                                >
                                  Due {dueShort}
                                </div>
                              ) : null;
                            }
                            return paidShort ? (
                              <div style={{ fontSize: 11, color: C.blueGray }}>
                                Paid {paidShort}
                              </div>
                            ) : null;
                          })()}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            textAlign: 'right',
                            color: amtColor,
                          }}
                        >
                          {isTrial ? 'FREE' : `$${p.amount}`}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            textAlign: 'right',
                            color: C.blueGray,
                          }}
                        >
                          {p.duration || '—'}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '2px 5px',
                              borderRadius: 3,
                              background: badgeBg,
                              color: badgeColor,
                            }}
                          >
                            {label}
                          </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {isPending && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              disableElevation
                              onClick={() =>
                                this.handlePayUnpaid(
                                  p.__synthetic ? { ...p, paymentId: null } : p
                                )
                              }
                              style={{
                                fontSize: 11,
                                textTransform: 'none',
                                borderRadius: 6,
                                padding: '2px 8px',
                                minWidth: 0,
                              }}
                            >
                              Pay
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      padding: 12,
                      textAlign: 'center',
                      color: C.blueGray,
                      fontSize: 12,
                    }}
                  >
                    No payments found
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── TEAM TAB ── */}
          {tab === 'team' && (
            <>
              {/* KPI Summary */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {[
                  { n: ownerCount, label: 'OWNERS', color: C.gold },
                  { n: mgrCount, label: 'MANAGERS', color: C.royalBlue },
                  { n: empCount, label: 'EMPLOYEES', color: C.empGray },
                ].map((k) => (
                  <div
                    key={k.label}
                    style={{
                      flex: 1,
                      background: C.card,
                      borderRadius: 10,
                      padding: '10px 12px',
                      textAlign: 'center',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                    }}
                  >
                    <div
                      style={{ fontSize: 18, fontWeight: 800, color: C.navy }}
                    >
                      {k.n}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: k.color,
                        letterSpacing: 0.5,
                      }}
                    >
                      {k.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Company Owners */}
              {owners.length > 0 && (
                <div style={cardStyle}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: C.gold,
                      marginBottom: 8,
                    }}
                  >
                    COMPANY OWNERS
                  </div>
                  {owners.map((o, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 0',
                        borderBottom:
                          i < owners.length - 1
                            ? `1px solid ${C.rowDiv}`
                            : 'none',
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          flexShrink: 0,
                          background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {getInitials(o.name)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: C.navy,
                          }}
                        >
                          {o.name}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.gold,
                          }}
                        >
                          Company Owner
                        </div>
                      </div>
                      {o.phone && (
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.royalBlue,
                          }}
                        >
                          {o.phone}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Managers */}
              {managers.length > 0 && (
                <div style={cardStyle}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: C.royalBlue,
                      marginBottom: 8,
                    }}
                  >
                    MANAGERS
                  </div>
                  {managers.map((m, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 0',
                        borderBottom:
                          i < managers.length - 1
                            ? `1px solid ${C.rowDiv}`
                            : 'none',
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          flexShrink: 0,
                          background: '#eef2ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: C.royalBlue,
                          fontSize: 11,
                          fontWeight: 800,
                        }}
                      >
                        {getInitials(m.name)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: C.navy,
                          }}
                        >
                          {m.name}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.royalBlue,
                          }}
                        >
                          Manager
                        </div>
                      </div>
                      {m.phone && (
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.royalBlue,
                          }}
                        >
                          {m.phone}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Employees */}
              {employees.length > 0 && (
                <div style={cardStyle}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: C.empGray,
                      marginBottom: 8,
                    }}
                  >
                    EMPLOYEES
                  </div>
                  {/* Header */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '32px 1fr 50px 60px',
                      padding: '4px 0',
                      borderBottom: `1.5px solid ${C.border}`,
                      fontSize: 11,
                      fontWeight: 800,
                      color: C.blueGray,
                      textTransform: 'uppercase',
                    }}
                  >
                    <div />
                    <div>Name</div>
                    <div style={{ textAlign: 'right' }}>Rate</div>
                    <div style={{ textAlign: 'right' }}>Role</div>
                  </div>
                  {/* Rows */}
                  {employees.map((emp, i) => {
                    const role = inferRole(emp);
                    const roleColor = ROLE_COLORS[role] || C.empGray;
                    const hasRate =
                      emp.hourlyRate && Number(emp.hourlyRate) > 0;
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '32px 1fr 50px 60px',
                          padding: '6px 0',
                          alignItems: 'center',
                          borderBottom:
                            i < employees.length - 1
                              ? `1px solid ${C.rowDiv}`
                              : 'none',
                        }}
                      >
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 7,
                            background: `${roleColor}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: roleColor,
                            fontSize: 10,
                            fontWeight: 800,
                          }}
                        >
                          {(emp.name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: C.navy,
                            }}
                          >
                            {emp.name}
                          </div>
                          {emp.rateLastUpdated && (
                            <div style={{ fontSize: 11, color: C.blueTint }}>
                              {fmtDate(emp.rateLastUpdated)}
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            textAlign: 'right',
                            color: hasRate ? C.navy : C.blueTint,
                          }}
                        >
                          {hasRate ? `$${emp.hourlyRate}/hr` : '--'}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '2px 5px',
                              borderRadius: 3,
                              background: `${roleColor}12`,
                              color: roleColor,
                            }}
                          >
                            {ROLE_LABELS[role]}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div
                    style={{
                      textAlign: 'center',
                      marginTop: 8,
                      fontSize: 12,
                      color: C.blueGray,
                    }}
                  >
                    Manage employees in Settings
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Pay-from-history dialog. Opened when the admin clicks Pay on an
            UNPAID row. Shows a picker of saved cards so the admin can charge
            an existing card without re-entering details; falls through to a
            new-card flow when needed. Passes paymentId so the server promotes
            this specific pending row to 'collected' instead of creating a
            duplicate row. After success, handlePaySuccess re-fetches
            locationDetail so the badge flips PAID. */}
        <SavedCardPaymentDialog
          open={this.state.payDialogOpen}
          companyId={company?.companyId || location?.companyId}
          amount={this.state.payRow?.amount}
          paymentId={this.state.payRow?.paymentId}
          onClose={this.handlePayDialogClose}
          onSuccess={this.handlePaySuccess}
        />
      </div>
    );
  }
}

const cardStyle = {
  background: '#ffffff',
  borderRadius: 12,
  padding: '14px 16px',
  marginBottom: 10,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

export default LocationDetailView;
